import { createApi } from "@reduxjs/toolkit/query/react";

import IScenarioBlockModifyRequest from "../../interfaces/request/scenario-block-modify-request.interface";
import axiosBaseQuery, { getToken } from "../root";

const baseUrl = `${
  import.meta.env.VITE_API_DOMAIN ? import.meta.env.VITE_API_DOMAIN : "/api"
}`;

const baseQuery = axiosBaseQuery({
  baseUrl
});

export const ScenarioRTKService = createApi({
  reducerPath: "ScenarioRTKService",
  baseQuery,
  tagTypes: ["GetScenarioDetail"],
  endpoints: (builder) => ({
    getAllScenarios: builder.query<any, void>({
      query: () => ({
        url: `/v3/scenario`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getScenarioDetail: builder.query<any, string>({
      query: (scenarioId) => ({
        url: `/v3/scenario/${scenarioId}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: (result, error, scenarioId) => [
        { type: "GetScenarioDetail", scenarioId }
      ],
      transformResponse: (response: any) => response.element
    }),
    getMcBotSample: builder.query<any, void>({
      query: () => ({
        url: `/v3/scenario/mcbot`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    setDefaultScenarios: builder.mutation<any, string>({
      query: (scenarioId) => ({
        url: `/v3/scenario/default/${scenarioId}`,
        method: "PUT",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    publishScenarios: builder.mutation<any, string>({
      query: (scenarioId) => ({
        url: `/v3/scenario/publish/${scenarioId}`,
        method: "PUT",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    createNewScenario: builder.mutation<
      any,
      {
        title: string;
        bot_code: string;
        template_id: number;
      }
    >({
      query: ({ title, bot_code, template_id }) => ({
        url: `/v3/scenario`,
        method: "POST",
        data: {
          title,
          bot_code,
          template_id
        },
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    modifyScenario: builder.mutation<
      any,
      { scenarioId: string; data: IScenarioBlockModifyRequest }
    >({
      query: ({ scenarioId, data }) => ({
        url: `/v3/scenario/${scenarioId}`,
        method: "PATCH",
        data: { data },
        formData: true,
        headers: {
          "x-auth-token": getToken()
        },
        maxBodyLength: 150 * 1024 * 1024 // 150MB
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "GetScenarioDetail", scenarioId: arg.scenarioId }
      ],
      transformResponse: (response) => response.element
    }),
    deleteScenario: builder.mutation<any, string>({
      query: (scenarioId) => ({
        url: `/v3/scenario/${scenarioId}`,
        method: "DELETE",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    getLinkTypes: builder.query({
      query: () => ({
        url: `/v3/scenario/icons`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element.icons
    })
  })
});

export const {
  useGetAllScenariosQuery,
  useSetDefaultScenariosMutation,
  usePublishScenariosMutation,
  useCreateNewScenarioMutation,
  useDeleteScenarioMutation,
  useModifyScenarioMutation,
  useGetMcBotSampleQuery,
  useGetScenarioDetailQuery,
  useLazyGetScenarioDetailQuery,
  useGetLinkTypesQuery
} = ScenarioRTKService;
